import { useMemo, useRef, useState } from "react";
import ApiKeyScreen from "./components/ApiKeyScreen";
import AppHeader from "./components/AppHeader";
import GenerationSidebar from "./components/GenerationSidebar";
import PassageView from "./components/PassageView";
import SentenceDetectiveDrawer from "./components/SentenceDetectiveDrawer";
import WordQuizDrawer from "./components/WordQuizDrawer";
import {
  cacheWordQuiz,
  readCachedWordQuiz,
} from "./utils/wordQuizCache";
import "./App.css";

const EMPTY_WORD_QUIZ = {
  open: false,
  status: "idle",
  word: "",
  context: "",
  occurrenceId: "",
  data: null,
  selectedIndex: null,
  error: "",
};

function parseWords(value) {
  return [
    ...new Set(
      value
        .split(/[\s,，;；]+/)
        .map((word) => word.trim())
        .filter(Boolean),
    ),
  ].slice(0, 20);
}

function App() {
  const [openAIApiKey, setOpenAIApiKey] = useState("");
  const [wordInput, setWordInput] = useState("");
  const [topic, setTopic] = useState("悬疑");
  const [customTopic, setCustomTopic] = useState("");
  const [passageLength, setPassageLength] = useState(500);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState("");
  const [generation, setGeneration] = useState({
    topic: "",
    customTopic: "",
    words: [],
    passageLength: 500,
    actualWordCount: null,
  });
  const [passage, setPassage] = useState(null);
  const [wordQuiz, setWordQuiz] = useState(EMPTY_WORD_QUIZ);
  const [checkedOccurrenceIds, setCheckedOccurrenceIds] = useState(
    () => new Set(),
  );
  const [selectedSentence, setSelectedSentence] = useState("");
  const [detectiveSentence, setDetectiveSentence] = useState("");
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const quizRequestId = useRef(0);

  const words = useMemo(() => parseWords(wordInput), [wordInput]);

  function openAIRequestHeaders() {
    return {
      "Content-Type": "application/json",
      "X-OpenAI-API-Key": openAIApiKey,
    };
  }

  function closeWordQuiz() {
    quizRequestId.current += 1;
    setWordQuiz(EMPTY_WORD_QUIZ);
  }

  function markOccurrenceChecked(occurrenceId) {
    setCheckedOccurrenceIds((current) => {
      if (current.has(occurrenceId)) return current;

      const next = new Set(current);
      next.add(occurrenceId);
      return next;
    });
  }

  function openSentenceDetective() {
    if (!selectedSentence) return;

    closeWordQuiz();
    setDetectiveSentence(selectedSentence);
    setSelectedSentence("");
  }

  async function evaluateSentencePart(part, sentence, phrase) {
    const response = await fetch(`/api/sentence-detective/${part}`, {
      method: "POST",
      headers: openAIRequestHeaders(),
      body: JSON.stringify({ sentence, phrase }),
    });

    const contentType = response.headers?.get?.("content-type");
    if (contentType && !contentType.includes("application/json")) {
      throw new Error(
        "后端没有返回 JSON。请确认后端服务已部署并可用。",
      );
    }

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error?.message || "句子分析失败，请稍后再试。");
    }
    if (
      typeof data.isCorrect !== "boolean" ||
      typeof data.feedback !== "string" ||
      !data.feedback.trim()
    ) {
      throw new Error("后端返回的句子分析结果无效。");
    }

    return data;
  }

  async function summarizeSentence(sentence) {
    const response = await fetch("/api/sentence-detective/summary", {
      method: "POST",
      headers: openAIRequestHeaders(),
      body: JSON.stringify({ sentence }),
    });

    const contentType = response.headers?.get?.("content-type");
    if (contentType && !contentType.includes("application/json")) {
      throw new Error(
        "后端没有返回 JSON。请确认后端服务已部署并可用。",
      );
    }

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error?.message || "句子总结失败，请稍后再试。");
    }
    if (typeof data.summary !== "string" || !data.summary.trim()) {
      throw new Error("后端返回的句子总结无效。");
    }

    return data;
  }

  async function openWordQuiz(word, context, occurrenceId) {
    const requestId = quizRequestId.current + 1;
    quizRequestId.current = requestId;

    const cachedData = readCachedWordQuiz(word, context, occurrenceId);
    if (cachedData) {
      setWordQuiz({
        ...EMPTY_WORD_QUIZ,
        open: true,
        status: "ready",
        word,
        context,
        occurrenceId,
        data: cachedData,
      });
      return;
    }

    setWordQuiz({
      ...EMPTY_WORD_QUIZ,
      open: true,
      status: "loading",
      word,
      context,
      occurrenceId,
    });

    try {
      const response = await fetch("/api/word-quiz", {
        method: "POST",
        headers: openAIRequestHeaders(),
        body: JSON.stringify({ word, context }),
      });

      const contentType = response.headers?.get?.("content-type");
      if (contentType && !contentType.includes("application/json")) {
        throw new Error(
          "后端没有返回 JSON。请确认后端服务已部署并可用。",
        );
      }

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error?.message || "词义练习生成失败，请稍后再试。");
      }

      cacheWordQuiz(word, context, occurrenceId, data);
      if (quizRequestId.current !== requestId) return;
      setWordQuiz((current) => ({
        ...current,
        status: "ready",
        data,
      }));
    } catch (error) {
      if (quizRequestId.current !== requestId) return;
      setWordQuiz((current) => ({
        ...current,
        status: "error",
        error:
          error instanceof Error
            ? error.message
            : "词义练习生成失败，请稍后再试。",
      }));
    }
  }

  function chooseMeaning(index) {
    if (
      wordQuiz.status !== "ready" ||
      wordQuiz.selectedIndex !== null ||
      !wordQuiz.data?.options?.[index]
    ) {
      return;
    }

    markOccurrenceChecked(wordQuiz.occurrenceId);
    setWordQuiz((current) => {
      if (current.status !== "ready" || current.selectedIndex !== null) {
        return current;
      }
      return { ...current, selectedIndex: index };
    });
  }

  async function handleGenerate() {
    if (words.length === 0 || (topic === "自定义" && !customTopic.trim())) {
      return;
    }

    setIsGenerating(true);
    setGenerationError("");

    try {
      const resolvedTopic = topic === "自定义" ? customTopic.trim() : topic;
      const response = await fetch("/api/passages", {
        method: "POST",
        headers: openAIRequestHeaders(),
        body: JSON.stringify({
          targetWords: words,
          topic: resolvedTopic,
          targetWordCount: passageLength,
        }),
      });

      const contentType = response.headers?.get?.("content-type");
      if (contentType && !contentType.includes("application/json")) {
        throw new Error(
          "后端没有返回 JSON。请确认后端服务已部署并可用。",
        );
      }

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error?.message || "文章生成失败，请稍后再试。");
      }

      closeWordQuiz();
      setCheckedOccurrenceIds(new Set());
      setSelectedSentence("");
      setDetectiveSentence("");
      setPassage(data);
      setGeneration({
        topic,
        customTopic: customTopic.trim(),
        words: data.targetWords,
        passageLength: data.targetWordCount,
        actualWordCount: data.actualWordCount,
      });
    } catch (error) {
      setGenerationError(
        error instanceof Error ? error.message : "文章生成失败，请稍后再试。",
      );
    } finally {
      setIsGenerating(false);
    }
  }

  if (!openAIApiKey) {
    return <ApiKeyScreen onSubmit={setOpenAIApiKey} />;
  }

  return (
    <div className="app-shell">
      <AppHeader onChangeApiKey={() => setOpenAIApiKey("")} />

      <main
        className={
          isSidebarCollapsed ? "workspace sidebar-collapsed" : "workspace"
        }
      >
        <GenerationSidebar
          isCollapsed={isSidebarCollapsed}
          wordInput={wordInput}
          wordCount={words.length}
          topic={topic}
          customTopic={customTopic}
          passageLength={passageLength}
          isGenerating={isGenerating}
          onWordInputChange={setWordInput}
          onTopicChange={setTopic}
          onCustomTopicChange={setCustomTopic}
          onPassageLengthChange={setPassageLength}
          onGenerate={handleGenerate}
          onToggleCollapsed={() =>
            setIsSidebarCollapsed((current) => !current)
          }
        />

        <PassageView
          passage={passage}
          generation={generation}
          requestedLength={passageLength}
          isGenerating={isGenerating}
          generationError={generationError}
          checkedOccurrenceIds={checkedOccurrenceIds}
          selectedSentence={selectedSentence}
          onWordClick={openWordQuiz}
          onSentenceSelect={setSelectedSentence}
          onOpenSentenceDetective={openSentenceDetective}
          onClearSentenceSelection={() => setSelectedSentence("")}
        />
      </main>

      {wordQuiz.open && (
        <WordQuizDrawer
          quiz={wordQuiz}
          onClose={closeWordQuiz}
          onRetry={() =>
            openWordQuiz(
              wordQuiz.word,
              wordQuiz.context,
              wordQuiz.occurrenceId,
            )
          }
          onChooseMeaning={chooseMeaning}
        />
      )}

      {detectiveSentence && (
        <SentenceDetectiveDrawer
          sentence={detectiveSentence}
          onEvaluate={evaluateSentencePart}
          onSummarize={summarizeSentence}
          onClose={() => setDetectiveSentence("")}
        />
      )}
    </div>
  );
}

export default App;
